import React, { createContext, useContext, useState } from 'react';
import * as yup from 'yup';

const PAGE_SIZE = 15;

const schema = {
  botId: yup.string(),
  vaultId: yup.string(),
  tokenAddress: yup.string(),
  sort: yup.string().default('-updatedAt').required(),
  limit: yup.number().default(PAGE_SIZE),
  offset: yup.number().default(0),
};

export type FilterState = yup.TypeFromShape<typeof schema, any>;

const defaultState: FilterState = {
  botId: undefined,
  vaultId: undefined,
  tokenAddress: undefined,
  sort: '-updatedAt',
  limit: PAGE_SIZE,
  offset: 0,
};

const FilterContext = createContext<{
  filtersState: FilterState;
  setFilters: (filters: Partial<FilterState>) => void;
  setSort: (key: string) => void;
  goToPage: (page: number) => void;
} | null>(null);

export const OpenTradesFilterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [filtersState, setFiltersState] = useState<FilterState>(defaultState);

  const setFilters = (newFilters: Partial<FilterState>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  };

  const goToPage = (page: number) => {
    setFilters({ offset: (page - 1) * filtersState.limit });
  };

  const setSort = (key: string) => {
    const current = filtersState.sort;
    let newSortKey = key;

    if (current?.includes(key)) {
      newSortKey = current.charAt(0) === '-' ? key : `-${key}`;
    }

    setFilters({ sort: newSortKey });
  };

  return (
    <FilterContext.Provider
      value={{ filtersState, setFilters, setSort, goToPage }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useOpenTradesSearchFilters = () => {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error(
      'useOpenTradesSearchFilters must be used within OpenTradesFilterProvider'
    );
  return ctx;
};
